import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  createNewsletter,
  getAdminNewsletter,
  updateNewsletter,
} from '../../../services/admin/newsletterApi';
import { AdminPage, AdminPageHeader, AdminPageTitle, AdminSection } from '../common/AdminPrimitives';
import { PrimaryButton } from '../common/AdminModalPrimitives';
import {
  ActionRow,
  ErrorMessage,
  FieldGroup,
  FieldLabel,
  FieldRow,
  ImagePreview,
  limitLines,
  TextArea,
  TextButton,
  TextInput,
  UploadButton,
  UploadCaption,
  UploadRow,
} from './NewsletterStyles';

interface NewsletterForm {
  externalUrl: string;
  title: string;
  author: string;
  tags: string;
  thumbnailUrl: string;
  thumbnailFile: File | null;
}

const EMPTY_FORM: NewsletterForm = {
  externalUrl: '',
  title: '',
  author: '',
  tags: '',
  thumbnailUrl: '',
  thumbnailFile: null,
};

const NewsletterRegisterComponent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const newsletterIdParam = searchParams.get('newsletterId');
  const newsletterId = newsletterIdParam ? Number(newsletterIdParam) : null;
  const isEdit = newsletterId !== null && !Number.isNaN(newsletterId);

  const [form, setForm] = useState<NewsletterForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchNewsletter = useCallback(async () => {
    if (!isEdit || newsletterId === null) return;

    try {
      setLoading(true);
      const newsletter = await getAdminNewsletter(newsletterId);
      setForm({
        externalUrl: newsletter.external_url ?? '',
        title: newsletter.title ?? '',
        author: newsletter.author ?? '',
        tags: newsletter.tags ?? '',
        thumbnailUrl: newsletter.thumbnail_url ?? '',
        thumbnailFile: null,
      });
    } catch (err: any) {
      setError(err.message || '뉴스레터를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  }, [isEdit, newsletterId]);

  useEffect(() => {
    fetchNewsletter();
  }, [fetchNewsletter]);

  const updateField = (patch: Partial<NewsletterForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleThumbnailChange = (file: File | null) => {
    if (!file) return;
    updateField({ thumbnailFile: file, thumbnailUrl: URL.createObjectURL(file) });
  };

  const handleSubmit = async (isTemp: boolean) => {
    if (!isTemp) {
      if (!form.title.trim()) {
        alert('제목을 입력해주세요.');
        return;
      }
      if (!form.externalUrl.trim()) {
        alert('스티비 링크를 입력해주세요.');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        title: form.title,
        author: form.author,
        tags: form.tags,
        externalUrl: form.externalUrl,
        isTemp,
        thumbnailImage: form.thumbnailFile,
      };

      if (isEdit && newsletterId !== null) {
        await updateNewsletter(newsletterId, payload);
      } else {
        await createNewsletter(payload);
      }

      alert(isTemp ? '임시 저장되었습니다.' : '등록되었습니다.');
      navigate('/admin/newsletter');
    } catch (err: any) {
      setError(err.message || '저장에 실패했습니다.');
      alert(err.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminPage>
        <AdminPageHeader>
          <AdminPageTitle>{isEdit ? '뉴스레터 수정' : '뉴스레터 등록'}</AdminPageTitle>
        </AdminPageHeader>
        <StateMessage>불러오는 중...</StateMessage>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader>
        <AdminPageTitle>{isEdit ? '뉴스레터 수정' : '뉴스레터 등록'}</AdminPageTitle>
      </AdminPageHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <AdminSection>
        <FieldGroup>
          <FieldLabel htmlFor="newsletter-external-url">스티비 링크</FieldLabel>
          <TextInput
            id="newsletter-external-url"
            value={form.externalUrl}
            onChange={(e) => updateField({ externalUrl: e.target.value })}
            placeholder="스티비 뉴스레터 링크를 입력해주세요."
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="newsletter-title">제목</FieldLabel>
          <TextArea
            id="newsletter-title"
            rows={2}
            value={form.title}
            onChange={(e) => updateField({ title: limitLines(e.target.value, 2) })}
            placeholder="제목을 입력해주세요. (최대 2줄)"
          />
        </FieldGroup>

        <FieldRow>
          <FieldGroup>
            <FieldLabel htmlFor="newsletter-author">작성자</FieldLabel>
            <TextInput
              id="newsletter-author"
              value={form.author}
              onChange={(e) => updateField({ author: e.target.value })}
              placeholder="작성자를 입력해주세요."
            />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel htmlFor="newsletter-tags">태그</FieldLabel>
            <TextInput
              id="newsletter-tags"
              value={form.tags}
              onChange={(e) => updateField({ tags: e.target.value })}
              placeholder="태그1, 태그2, 태그3, ..."
            />
          </FieldGroup>
        </FieldRow>

        <FieldGroup>
          <FieldLabel>카드 썸네일</FieldLabel>
          <UploadRow>
            <UploadCaption>가로 588px, 세로 440px의 JPG, PNG 파일 업로드</UploadCaption>
            <UploadButton type="button" onClick={() => fileInputRef.current?.click()}>
              이미지 업로드
            </UploadButton>
          </UploadRow>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => handleThumbnailChange(e.target.files?.[0] ?? null)}
          />
          {form.thumbnailUrl && (
            <ImagePreview $ratio="588 / 440">
              <img src={form.thumbnailUrl} alt="카드 썸네일" />
            </ImagePreview>
          )}
        </FieldGroup>
      </AdminSection>

      <ActionRow>
        <TextButton type="button" onClick={() => handleSubmit(true)} disabled={saving}>
          임시 저장
        </TextButton>
        <PrimaryButton type="button" onClick={() => handleSubmit(false)} disabled={saving}>
          {saving ? '저장 중...' : isEdit ? '수정하기' : '등록하기'}
        </PrimaryButton>
      </ActionRow>
    </AdminPage>
  );
};

const HiddenFileInput = styled.input`
  display: none;
`;

const StateMessage = styled.div`
  padding: 40px;
  color: #666;
  font-size: 16px;
  text-align: center;
`;

export default NewsletterRegisterComponent;
